import { Dispatch, ReactNode, SetStateAction, useMemo } from 'react';
import {
  ActiveTableFilterOption,
  ActiveTableToggle,
  BasicTableDataRow,
  CustomFilterMap,
  TableCellContentAlignEnum,
  TableDataRow,
  TableFilter,
  TableHeaderItem,
  TableSortOption,
  TableToggle,
} from '@employ-engage/data';

import classNames from 'classnames';
import TableRow from './table-row';
import FilterBar from '../filter-bar/filter-bar';
import groupBy from 'lodash.groupby';
import uniqBy from 'lodash.uniqby';

export interface NewTableProps<T extends BasicTableDataRow> {
  onClickRow?: (row: T) => void;
  backgroundColor?: string;
  mapData?: (
    row: TableDataRow<T>,
    index: number
  ) => TableDataRow<T> & { [key: string]: ReactNode };
  headerList: TableHeaderItem[];
  dataList: TableDataRow<T>[];
  actionItems?: ReactNode;
  className?: string;
  label?: string;
  sortOptions?: TableSortOption[];
  activeSort?: TableSortOption;
  setActiveSort?: Dispatch<SetStateAction<TableSortOption>>;
  customFilterMap?: CustomFilterMap;
  filters?: TableFilter[];
  activeFilters?: ActiveTableFilterOption[];
  setActiveFilters?: Dispatch<SetStateAction<ActiveTableFilterOption[]>>;
  toggles?: TableToggle[];
  activeToggles?: ActiveTableToggle[];
  setActiveToggles?: Dispatch<SetStateAction<ActiveTableToggle[]>>;
  searchValue?: string;
  handleSearch?: () => void;
  hideCount?: boolean;
}

export function Table<T extends BasicTableDataRow>({
  onClickRow,
  backgroundColor,
  dataList,
  mapData,
  headerList,
  actionItems,
  className,
  label,
  sortOptions,
  activeSort,
  setActiveSort,
  customFilterMap,
  filters,
  activeFilters,
  setActiveFilters,
  toggles,
  activeToggles,
  setActiveToggles,
  searchValue,
  handleSearch,
  hideCount = false,
}: NewTableProps<T>) {
  const filteredDataList = useMemo(() => {
    if ((!activeFilters || activeFilters.length === 0) && !activeToggles) {
      return dataList;
    }

    const filteredList = dataList.filter((row) => {
      const groupsOfActiveFilters = groupBy(activeFilters, 'parentId');
      const resultsOfFilteredRowGroups = [];
      for (const key in groupsOfActiveFilters) {
        const groupOfActiveFilters = groupsOfActiveFilters[key];
        resultsOfFilteredRowGroups.push(
          groupOfActiveFilters?.reduce(
            (
              acc: boolean,
              curr: ActiveTableFilterOption,
              index: number,
              activeFilters
            ) => {
              if (index === 0) {
                return !!curr.filter?.(row, activeFilters);
              }
              return acc || !!curr.filter?.(row, activeFilters);
            },
            true
          ) || groupOfActiveFilters?.length === 0
        );
      }

      return resultsOfFilteredRowGroups.every((group) => group);
    });

    if (toggles && activeToggles) {
      const newFilteredList = [];
      for (const toggle of activeToggles) {
        newFilteredList.push(...filteredList.filter(toggle.filter));
      }

      const negativeToggles = toggles.filter(
        (toggle) =>
          !activeToggles?.map((toggle) => toggle.id)?.includes(toggle.id)
      );

      for (const toggle of negativeToggles) {
        newFilteredList.push(...filteredList.filter(toggle.negativeFilter));
      }

      return uniqBy(newFilteredList, 'id');
    }
    return filteredList;
  }, [activeFilters, activeToggles, dataList, toggles]);

  const sortedDataList = useMemo(() => {
    if (!activeSort) {
      return filteredDataList;
    }

    return filteredDataList.slice().sort(activeSort.sort);
  }, [activeSort, filteredDataList]);

  return (
    <div
      className={classNames(
        className,
        "text-neutral-900 font-['Poppins'] bg-white rounded-2xl shadow overflow-hidden"
      )}
    >
      <div className="mx-[23.8px] mt-[25.92px] mb-[28.08px] flex justify-between items-center">
        <div>
          <div className="text-[15px] font-medium font-['Poppins'] leading-snug tracking-tight">
            {label && hideCount
              ? label
              : label
              ? `${label} (${sortedDataList.length})`
              : ''}
          </div>
          {handleSearch && (
            <div className="min-w-[340px] w-[400px] h-[49px] p-[14.53px] bg-stone-50 rounded-2xl justify-start items-center gap-[10.90px] inline-flex group ring-1 ring-gray-300">
              <i className="fa-solid fa-magnifying-glass w-[22px] h-[22px] relative" />
              <input
                value={searchValue}
                className="grow shrink basis-0 text-neutral-600 text-[15px] font-normal font-['Poppins'] leading-snug tracking-tight bg-stone-50 border-none outline-none"
                placeholder="Search Forms..."
              />
            </div>
          )}
        </div>
        <div>{actionItems}</div>
      </div>
      {(sortOptions || filters) && (
        <FilterBar
          customFilterMap={customFilterMap}
          sortOptions={sortOptions}
          activeSort={activeSort}
          setActiveSort={setActiveSort}
          filters={filters}
          activeFilters={activeFilters}
          setActiveFilters={setActiveFilters}
          toggles={toggles}
          activeToggles={activeToggles}
          setActiveToggles={setActiveToggles}
        />
      )}

      <div className="w-full overflow-auto">
        <table
          className={classNames(
            'border-b border-neutral-300 bg-white w-full',
            className
          )}
        >
          <thead className="px-[19.72px] py-[16.13px]">
            <tr className="relative border-b border-neutral-300">
              {headerList.map((headerItem) => (
                <th
                  key={headerItem.id}
                  scope="col"
                  style={headerItem?.headerStyles || {}}
                  className={classNames(
                    "py-[16.13px] px-[19.72px] text-neutral-900 text-sm font-semibold font-['Poppins'] leading-[17.93px] tracking-tight",
                    {
                      'text-left':
                        !headerItem.alignment ||
                        headerItem.alignment === TableCellContentAlignEnum.LEFT,
                      'text-center':
                        headerItem.alignment ===
                        TableCellContentAlignEnum.CENTER,
                      'text-right':
                        headerItem.alignment ===
                        TableCellContentAlignEnum.RIGHT,
                    }
                  )}
                >
                  {headerItem.headerLabel}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {sortedDataList.map((row, index) => {
              let modifiedRow;
              if (mapData) {
                modifiedRow = mapData(row, index);
              }
              return (
                <TableRow
                  onClickRow={onClickRow}
                  backgroundColor={backgroundColor}
                  key={index}
                  headerList={headerList}
                  row={modifiedRow || row}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Table;
